import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { HttpService } from 'core/services/http';
import { CVHttpService } from 'features/feature_cv/services';
import { Utils } from 'core/utils';
import {
    Box, Container, Grid, Card, CardContent, CardActionArea,
    Typography, Button, Paper, CircularProgress, Chip, Stack,
    Avatar, Divider
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const DECISION_CONFIG = {
    shortlisted: { label: 'Shortlisted', color: 'success' },
    rejected:    { label: 'Rejected',    color: 'error'   },
    hired:       { label: 'Hired',       color: 'primary' },
};
const decisionColor = (d) => DECISION_CONFIG[d]?.color ?? 'default';
const decisionLabel = (d) => DECISION_CONFIG[d]?.label ?? 'Pending';

const AVATAR_COLORS = ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c62828', '#00838f'];
const nameToColor = (name = '') => {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

function StatCard({ icon, label, value, loading, to }) {
    return (
        <Card
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}
        >
            <CardActionArea component={Link} to={to} sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Box sx={{
                    width: 40, height: 40, borderRadius: 1.5,
                    bgcolor: 'primary.50', color: 'primary.main',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5,
                }}>
                    {icon}
                </Box>
                <Typography variant="h4" fontWeight={700} lineHeight={1}>
                    {loading ? '—' : value}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {label}
                </Typography>
            </CardActionArea>
        </Card>
    );
}

function JobseekerDashboard() {
    const navigate = useNavigate();
    const user = useMemo(() => HttpService.getUser?.() ?? null, []);

    const { data: applicationsData, isLoading: applicationsLoading } = useQuery(
        'myApplications',
        async () => {
            const service = new HttpService();
            return await service.get('/job/apply/', null);
        }
    );

    const { data: cvsData, isLoading: cvsLoading } = useQuery(
        'myCVs',
        async () => CVHttpService.loadCVList({ page: 1, size: 10 })
    );

    const applications = applicationsData?.results || applicationsData || [];
    const cvs = cvsData?.results || cvsData || [];

    const statusCounts = useMemo(() => ({
        pending:     applications.filter(a => !a.final_decision || a.final_decision === 'pending').length,
        shortlisted: applications.filter(a => a.final_decision === 'shortlisted').length,
        hired:       applications.filter(a => a.final_decision === 'hired').length,
        rejected:    applications.filter(a => a.final_decision === 'rejected').length,
    }), [applications]);

    const firstName = user?.first_name || user?.email?.split('@')[0] || 'there';

    return (
        <Container maxWidth="lg">
            {/* Welcome */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" fontWeight={700}>
                    Welcome back, {firstName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Here's a summary of your job search activity.
                </Typography>
            </Box>

            {/* Stat cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={4}>
                    <StatCard
                        icon={<WorkOutlineIcon fontSize="small" />}
                        label="Applications"
                        value={applications.length}
                        loading={applicationsLoading}
                        to="/jobseeker/applications"
                    />
                </Grid>
                <Grid item xs={6} sm={4}>
                    <StatCard
                        icon={<DescriptionOutlinedIcon fontSize="small" />}
                        label="CVs"
                        value={cvs.length}
                        loading={cvsLoading}
                        to="/jobseeker/cvs"
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <StatCard
                        icon={<PersonOutlineIcon fontSize="small" />}
                        label="Profile"
                        value="View"
                        loading={false}
                        to="/jobseeker/profile"
                    />
                </Grid>
            </Grid>

            <Grid container spacing={2.5}>
                {/* Recent Applications */}
                <Grid item xs={12} md={8}>
                    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                        <Box sx={{ px: 2.5, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                Recent Applications
                            </Typography>
                            <Button
                                component={Link}
                                to="/jobseeker/applications"
                                size="small"
                                endIcon={<ArrowForwardIcon fontSize="small" />}
                                sx={{ textTransform: 'none' }}
                            >
                                View all
                            </Button>
                        </Box>
                        <Divider />

                        {applicationsLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                                <CircularProgress size={28} />
                            </Box>
                        ) : applications.length === 0 ? (
                            <Box sx={{ py: 5, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    No applications yet.
                                </Typography>
                                <Button
                                    component={Link}
                                    to="/jobs"
                                    variant="contained"
                                    disableElevation
                                    size="small"
                                    sx={{ mt: 1 }}
                                >
                                    Browse Jobs
                                </Button>
                            </Box>
                        ) : (
                            <Box>
                                {applications.slice(0, 6).map((app, idx) => {
                                    const company = app.job?.company?.title || app.company_title || 'Unknown Company';
                                    return (
                                        <Box
                                            key={app.id}
                                            onClick={() => navigate(`/jobseeker/applications/${app.id}`)}
                                            sx={{
                                                px: 2.5, py: 1.75,
                                                display: 'flex', alignItems: 'center', gap: 1.5,
                                                cursor: 'pointer',
                                                borderBottom: idx < Math.min(applications.length, 6) - 1 ? '1px solid' : 'none',
                                                borderColor: 'divider',
                                                '&:hover': { bgcolor: 'action.hover' },
                                                transition: 'background 0.15s',
                                            }}
                                        >
                                            <Avatar
                                                variant="rounded"
                                                sx={{
                                                    width: 36, height: 36, fontSize: 14, fontWeight: 700,
                                                    bgcolor: nameToColor(company), flexShrink: 0,
                                                }}
                                            >
                                                {company.charAt(0).toUpperCase()}
                                            </Avatar>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="body2" fontWeight={600} noWrap>
                                                    {app.job?.title || 'Unknown Job'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" noWrap display="block">
                                                    {company}
                                                </Typography>
                                            </Box>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                                                <Chip
                                                    label={decisionLabel(app.final_decision)}
                                                    color={decisionColor(app.final_decision)}
                                                    size="small"
                                                    sx={{ height: 20, fontSize: 11 }}
                                                />
                                                <Typography variant="caption" color="text.disabled" sx={{ display: { xs: 'none', sm: 'block' } }}>
                                                    {app.created_at ? Utils.timeAgo(app.created_at) : ''}
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Paper>
                </Grid>

                {/* Right column */}
                <Grid item xs={12} md={4}>
                    <Stack spacing={2}>
                        {/* Status breakdown */}
                        {applications.length > 0 && (
                            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5 }}>
                                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                    Application Status
                                </Typography>
                                <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                                    {[
                                        { label: 'Pending', count: statusCounts.pending, color: 'text.secondary' },
                                        { label: 'Shortlisted', count: statusCounts.shortlisted, color: 'success.main' },
                                        { label: 'Hired', count: statusCounts.hired, color: 'primary.main' },
                                        { label: 'Rejected', count: statusCounts.rejected, color: 'error.main' },
                                    ].filter(s => s.count > 0).map(s => (
                                        <Box key={s.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                                            <Typography variant="body2" fontWeight={600} color={s.color}>{s.count}</Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </Paper>
                        )}

                        {/* Quick actions */}
                        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5 }}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                Quick Actions
                            </Typography>
                            <Stack spacing={1} sx={{ mt: 1.5 }}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<SearchIcon />}
                                    component={Link}
                                    to="/jobs"
                                    disableElevation
                                    size="small"
                                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                                >
                                    Browse Jobs
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<AddIcon />}
                                    component={Link}
                                    to="/cv/builder"
                                    disableElevation
                                    size="small"
                                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                                >
                                    Create New CV
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<AutoAwesomeIcon />}
                                    component={Link}
                                    to="/cv/ai-builder"
                                    disableElevation
                                    size="small"
                                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                                >
                                    Create CV with AI
                                </Button>
                            </Stack>
                        </Paper>
                    </Stack>
                </Grid>
            </Grid>
        </Container>
    );
}

export default JobseekerDashboard;
